/**
 * Dialog for uploading a Job Description (file or raw text) and triggering AI parsing.
 */

import { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, Loader2, AlertCircle, X } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Alert,
  AlertDescription,
  MultiSelectCombobox,
} from '@aaas/shared';
import { useAuth } from '@aaas/shared';
import { useJobListQuery } from '../hooks/useJobQueries';
import jobDescriptionService from '../services/job-description.service';

interface JDUploadDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-selected job ID — hides job selector when provided */
  jobId?: string;
  onSuccess?: () => void;
}

type InputMode = 'file' | 'text';

export function JDUploadDialog({ open, onClose, jobId: propJobId, onSuccess }: JDUploadDialogProps) {
  const { t } = useTranslation('ats');
  const { currentWorkspace, idToken } = useAuth();

  const [mode, setMode] = useState<InputMode>('file');
  const [name, setName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [rawContent, setRawContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [isPending, setIsPending] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const jobListQuery = useJobListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: !propJobId && open,
    size: 100,
    refetchInterval: false,
  });

  const jobOptions = useMemo(() =>
    (jobListQuery.data?.data?.items ?? []).map(job => ({
      value: job.id,
      label: `${job.code} — ${job.position?.name ?? job.id}`,
    })),
    [jobListQuery.data]
  );

  const handleClose = () => {
    if (isPending) return;
    setMode('file');
    setName('');
    setSelectedFile(null);
    setRawContent('');
    setError(null);
    setSelectedJobIds([]);
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    if (file && !name) {
      setName(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!currentWorkspace || !idToken) return;

    setError(null);

    const targetJobIds = propJobId ? [propJobId] : selectedJobIds;

    if (!propJobId && targetJobIds.length === 0) {
      setError(t('jd.upload.errors.noJob'));
      return;
    }
    if (mode === 'file' && !selectedFile) {
      setError(t('jd.upload.errors.noFile'));
      return;
    }
    if (mode === 'text' && !rawContent.trim()) {
      setError(t('jd.upload.errors.noContent'));
      return;
    }

    setIsPending(true);
    try {
      for (const jId of targetJobIds) {
        await jobDescriptionService.uploadJobDescription(currentWorkspace.id, idToken, jId, {
          name: name.trim() || undefined,
          file: mode === 'file' ? (selectedFile ?? undefined) : undefined,
          raw_content: mode === 'text' ? rawContent.trim() : undefined,
        });
      }
      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('jd.upload.errors.uploadFailed'));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('jd.upload.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Mode toggle */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => { setMode('file'); setError(null); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors ${
                mode === 'file'
                  ? 'bg-orange-50 text-orange-700 border-r border-orange-200'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border-r border-gray-200'
              }`}
            >
              <Upload className="h-4 w-4" />
              {t('jd.upload.modeFile')}
            </button>
            <button
              type="button"
              onClick={() => { setMode('text'); setError(null); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors ${
                mode === 'text'
                  ? 'bg-orange-50 text-orange-700'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FileText className="h-4 w-4" />
              {t('jd.upload.modeText')}
            </button>
          </div>

          {/* Job selector — only when jobId not provided by parent */}
          {!propJobId && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                {t('jd.upload.jobLabel')} <span className="text-gray-400 font-normal">{t('jd.upload.jobLabelHint')}</span>
              </label>
              <MultiSelectCombobox
                options={jobOptions}
                values={selectedJobIds}
                onChange={setSelectedJobIds}
                placeholder={t('jd.upload.selectJobsPlaceholder')}
                displayName={t('jd.upload.jobsName')}
                searchPlaceholder={t('jd.upload.searchJobs')}
                emptyMessage={t('jd.upload.noJobsFound')}
                isLoading={jobListQuery.isLoading}
                className="w-full"
              />
            </div>
          )}

          {/* Name field */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              {t('jd.upload.nameLabel')} <span className="text-gray-400 font-normal">{t('jd.upload.nameLabelHint')}</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('jd.upload.namePlaceholder')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              disabled={isPending}
            />
          </div>

          {/* File input */}
          {mode === 'file' && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">{t('jd.upload.fileLabel')}</label>
              {selectedFile ? (
                <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700 flex-1 truncate">{selectedFile.name}</span>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={isPending}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-300 rounded-md hover:border-orange-400 hover:bg-orange-50 transition-colors"
                  disabled={isPending}
                >
                  <Upload className="h-8 w-8 text-gray-400" />
                  <div className="text-sm text-center">
                    <span className="text-orange-600 font-medium">{t('jd.upload.clickToSelect')}</span>
                    <span className="text-gray-500">{t('jd.upload.fileSuffix')}</span>
                  </div>
                  <p className="text-xs text-gray-400">{t('jd.upload.supportedFormats')}</p>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          )}

          {/* Raw text input */}
          {mode === 'text' && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">{t('jd.upload.contentLabel')}</label>
              <textarea
                value={rawContent}
                onChange={(e) => setRawContent(e.target.value)}
                placeholder={t('jd.upload.contentPlaceholder')}
                rows={8}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                disabled={isPending}
              />
              <p className="text-xs text-gray-400">{t('jd.upload.charactersCount', { count: rawContent.length })}</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <p className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
            {t('jd.upload.infoNote')}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            {t('jd.upload.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('jd.upload.uploading')}
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                {t('jd.upload.submit')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
